import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'
import { htmlToPlainText } from './richText'
import { getTicketType } from './ticketType'
import { IMPACT_LEVELS, URGENCY_LEVELS } from './priorityMatrix'

const labelOf = (levels, value) =>
  levels.find((l) => l.value === Number(value))?.label || '—'

// ─── Paleta ──────────────────────────────────────────────────────────────────
const C = {
  indigo: '#3730a3',
  indigoLight: '#6366f1',
  indigoPale: '#eef2ff',
  indigoMid: '#c7d2fe',
  slate900: '#0f172a',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  white: '#ffffff',
  green700: '#15803d',
  green100: '#dcfce7',
  green50: '#f0fdf4',
  amber600: '#d97706',
  amber100: '#fef3c7',
  red600: '#dc2626',
  red100: '#fee2e2',
  pink800: '#9d174d',
  pink100: '#fce7f3',
  blue700: '#1d4ed8',
  blue100: '#dbeafe',
  gray400: '#9ca3af',
  gray600: '#6b7280',
}

// ─── Estilos ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 44,
    paddingBottom: 56,
    paddingHorizontal: 44,
    backgroundColor: C.white,
    color: C.slate900,
  },

  // ── Encabezado ──────────────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: C.indigoLight,
  },
  headerLeft: { flex: 1, paddingRight: 24 },
  headerBadge: {
    backgroundColor: C.indigo,
    color: C.white,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 3,
    alignSelf: 'flex-start',
    marginBottom: 6,
    letterSpacing: 1.2,
  },
  headerTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 17,
    color: C.slate900,
    lineHeight: 1.3,
  },
  headerSub: {
    fontSize: 9,
    color: C.slate500,
    marginTop: 3,
  },
  headerRight: {
    alignItems: 'flex-end',
    gap: 5,
    minWidth: 140,
  },
  headerMeta: {
    fontSize: 8.5,
    color: C.slate500,
  },
  headerMetaBold: {
    fontFamily: 'Helvetica-Bold',
    color: C.slate700,
  },

  // ── Badges / etiquetas ──────────────────────────────────────────────────────
  badge: {
    borderRadius: 99,
    paddingVertical: 3,
    paddingHorizontal: 10,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    alignSelf: 'flex-start',
  },

  // ── Secciones ───────────────────────────────────────────────────────────────
  section: { marginBottom: 20 },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: C.slate200,
  },
  sectionAccent: {
    width: 3,
    height: 12,
    backgroundColor: C.indigoLight,
    borderRadius: 2,
    marginRight: 7,
  },
  sectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: C.indigo,
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  sectionCount: {
    marginLeft: 6,
    fontSize: 8,
    color: C.slate400,
    fontFamily: 'Helvetica',
  },

  // ── Grid de info ────────────────────────────────────────────────────────────
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  gridItem: {
    width: '47.5%',
    backgroundColor: C.slate50,
    borderRadius: 5,
    padding: 10,
    borderLeftWidth: 3,
    borderLeftColor: C.indigoMid,
  },
  gridLabel: {
    fontSize: 7.5,
    color: C.slate400,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.8,
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  gridValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: C.slate900,
  },

  // ── Descripción ─────────────────────────────────────────────────────────────
  descBox: {
    backgroundColor: C.slate50,
    borderRadius: 5,
    padding: 13,
    borderLeftWidth: 3,
    borderLeftColor: C.indigoLight,
  },
  descText: {
    fontSize: 9.5,
    color: C.slate700,
    lineHeight: 1.6,
  },

  // ── Comentarios ─────────────────────────────────────────────────────────────
  commentCard: {
    backgroundColor: C.slate50,
    borderRadius: 5,
    padding: 11,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: C.indigoMid,
  },
  commentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  commentAuthorWrap: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  commentDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: C.indigoLight,
  },
  commentAuthor: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: C.slate900,
  },
  commentDate: {
    fontSize: 8,
    color: C.slate400,
  },
  commentText: {
    fontSize: 9,
    color: C.slate700,
    lineHeight: 1.5,
  },
  commentFileLine: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    gap: 4,
  },
  commentFilePill: {
    backgroundColor: C.indigoPale,
    borderRadius: 3,
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  commentFileText: {
    fontSize: 7.5,
    color: C.indigo,
  },

  // ── Soluciones ──────────────────────────────────────────────────────────────
  solutionCard: {
    backgroundColor: C.green50,
    borderRadius: 5,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: C.green700,
    marginBottom: 8,
  },
  solutionTitleBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
    gap: 6,
  },
  solutionDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: C.green700,
  },
  solutionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9.5,
    color: C.green700,
  },
  solutionText: {
    fontSize: 9,
    color: C.slate700,
    lineHeight: 1.5,
  },
  solutionFilePill: {
    backgroundColor: C.green100,
    borderRadius: 3,
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  solutionFileText: {
    fontSize: 7.5,
    color: C.green700,
  },

  // ── Utilidades ──────────────────────────────────────────────────────────────
  emptyText: { fontSize: 9, color: C.slate400, fontStyle: 'italic' },
  row: { flexDirection: 'row', alignItems: 'center' },

  // ── Footer ───────────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: C.slate200,
    paddingTop: 7,
  },
  footerLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  footerDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.indigoLight,
  },
  footerText: { fontSize: 7.5, color: C.slate400 },
  footerBold: {
    fontFamily: 'Helvetica-Bold',
    color: C.slate500,
    fontSize: 7.5,
  },
})

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PRIORITY_MAP = {
  1: { label: 'Baja', bg: C.green100, color: C.green700 },
  2: { label: 'Media', bg: C.amber100, color: C.amber600 },
  3: { label: 'Alta', bg: C.red100, color: C.red600 },
  4: { label: 'Crítica', bg: C.pink100, color: C.pink800 },
}

const STATUS_MAP = {
  1: { label: 'Abierto', bg: C.blue100, color: C.blue700 },
  2: { label: 'En progreso', bg: C.amber100, color: C.amber600 },
  3: { label: 'Resuelto', bg: C.green100, color: C.green700 },
  4: { label: 'Cerrado', bg: C.slate200, color: C.slate500 },
}

const formatDate = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleString('es-CR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Sub-componentes ─────────────────────────────────────────────────────────
const SectionTitle = ({ children, count }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionAccent} />
    <Text style={styles.sectionTitle}>{children}</Text>
    {count !== undefined && <Text style={styles.sectionCount}>({count})</Text>}
  </View>
)

const FilePill = ({ name, size, green }) => (
  <View style={styles.commentFileLine}>
    <View style={green ? styles.solutionFilePill : styles.commentFilePill}>
      <Text style={green ? styles.solutionFileText : styles.commentFileText}>
        ⬡ {name}
        {size ? ` · ${formatSize(size)}` : ''}
      </Text>
    </View>
  </View>
)

// ─── Documento ───────────────────────────────────────────────────────────────
const TicketDocument = ({
  ticket,
  files,
  commentaries,
  solutions,
  userCreator,
}) => {
  const priority = PRIORITY_MAP[ticket.priorityId] || {
    label: `#${ticket.priorityId}`,
    bg: C.slate100,
    color: C.slate700,
  }
  const status = STATUS_MAP[ticket.statusId] || {
    label: `#${ticket.statusId}`,
    bg: C.slate100,
    color: C.slate700,
  }

  const filesByComment = (files || []).reduce((acc, f) => {
    if (f.commentId) {
      acc[f.commentId] = acc[f.commentId] || []
      acc[f.commentId].push(f)
    }
    return acc
  }, {})

  const creatorName = userCreator
    ? `${userCreator.nombre} ${userCreator.apellido || ''}`.trim()
    : '—'

  return (
    <Document title={`Ticket #${ticket.id} - ${ticket.title}`}>
      <Page size="A4" style={styles.page}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerBadge}>TICKET #{ticket.id}</Text>
            <Text style={styles.headerTitle}>{ticket.title}</Text>
            {ticket.systemOrigin && (
              <Text style={styles.headerSub}>
                Sistema: {ticket.systemOrigin}
              </Text>
            )}
          </View>
          <View style={styles.headerRight}>
            <Text
              style={[
                styles.badge,
                { backgroundColor: status.bg, color: status.color },
              ]}
            >
              {status.label}
            </Text>
            <Text
              style={[
                styles.badge,
                { backgroundColor: priority.bg, color: priority.color },
              ]}
            >
              {priority.label}
            </Text>
            <Text style={styles.headerMeta}>
              <Text style={styles.headerMetaBold}>Creado: </Text>
              {formatDate(ticket.createdAt)}
            </Text>
            {ticket.dueAt && (
              <Text style={styles.headerMeta}>
                <Text style={styles.headerMetaBold}>Vence: </Text>
                {formatDate(ticket.dueAt)}
              </Text>
            )}
          </View>
        </View>

        {/* ── Información General ─────────────────────────────────────────── */}
        <View style={styles.section}>
          <SectionTitle>Información General</SectionTitle>
          <View style={styles.grid}>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Tipo</Text>
              <Text style={styles.gridValue}>
                {getTicketType(ticket.type).label}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Creado Por</Text>
              <Text style={styles.gridValue}>{creatorName}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Puesto</Text>
              <Text style={styles.gridValue}>{userCreator?.puesto || '—'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Responsable</Text>
              <Text style={styles.gridValue}>
                {ticket.responsibleId || 'Sin asignar'}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Sistema</Text>
              <Text style={styles.gridValue}>{ticket.systemOrigin || '—'}</Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Impacto</Text>
              <Text style={styles.gridValue}>
                {labelOf(IMPACT_LEVELS, ticket.impact)}
              </Text>
            </View>
            <View style={styles.gridItem}>
              <Text style={styles.gridLabel}>Urgencia</Text>
              <Text style={styles.gridValue}>
                {labelOf(URGENCY_LEVELS, ticket.urgency)}
              </Text>
            </View>
            {ticket.resolvedAt && (
              <View style={styles.gridItem}>
                <Text style={styles.gridLabel}>Resuelto</Text>
                <Text style={[styles.gridValue, { color: C.green700 }]}>
                  {formatDate(ticket.resolvedAt)}
                </Text>
              </View>
            )}
            {ticket.closedAt && (
              <View style={styles.gridItem}>
                <Text style={styles.gridLabel}>Cerrado</Text>
                <Text style={[styles.gridValue, { color: C.slate500 }]}>
                  {formatDate(ticket.closedAt)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── Descripción ────────────────────────────────────────────────── */}
        <View style={styles.section}>
          <SectionTitle>Descripción</SectionTitle>
          <View style={styles.descBox}>
            <Text style={styles.descText}>
              {ticket.description || 'Sin descripción.'}
            </Text>
          </View>
        </View>

        {/* ── Comentarios ────────────────────────────────────────────────── */}
        <View style={styles.section}>
          <SectionTitle count={commentaries?.length || 0}>
            Comentarios
          </SectionTitle>
          {commentaries?.length > 0 ? (
            commentaries.map((c) => {
              const attachments = filesByComment[c.id] || []
              return (
                <View key={c.id} style={styles.commentCard}>
                  <View style={styles.commentHeader}>
                    <View style={styles.commentAuthorWrap}>
                      <View style={styles.commentDot} />
                      <Text style={styles.commentAuthor}>{creatorName}</Text>
                    </View>
                    <Text style={styles.commentDate}>
                      {formatDate(c.createdAt)}
                    </Text>
                  </View>
                  <Text style={styles.commentText}>{c.content}</Text>
                  {attachments.map((f) => (
                    <FilePill key={f.id} name={f.name} size={f.size} />
                  ))}
                </View>
              )
            })
          ) : (
            <Text style={styles.emptyText}>Sin comentarios registrados.</Text>
          )}
        </View>

        {/* ── Soluciones ─────────────────────────────────────────────────── */}
        {solutions?.length > 0 && (
          <View style={styles.section}>
            <SectionTitle count={solutions.length}>Soluciones</SectionTitle>
            {solutions.map((s) => (
              <View key={s.id} style={styles.solutionCard}>
                <View style={styles.solutionTitleBar}>
                  <View style={styles.solutionDot} />
                  <Text style={styles.solutionTitle}>{s.title}</Text>
                </View>
                <Text style={styles.solutionText}>
                  {htmlToPlainText(s.description)}
                </Text>
                {s.files?.map((f) => (
                  <FilePill key={f.id} name={f.name} size={f.size} green />
                ))}
              </View>
            ))}
          </View>
        )}

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <View style={styles.footerDot} />
            <Text style={styles.footerBold}>Ticket #{ticket.id}</Text>
            <Text style={styles.footerText}>
              · {ticket.systemOrigin || '—'} · {creatorName}
            </Text>
          </View>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )
}

// ─── Exportar ─────────────────────────────────────────────────────────────────
/**
 * Genera y descarga el PDF de un ticket.
 *
 * @param {Object} ticket
 * @param {Object} extra  - { files, commentaries, solutions, userCreator }
 */
const generatePDF = async (
  ticket,
  { files = [], commentaries = [], solutions = [], userCreator = null } = {}
) => {
  const blob = await pdf(
    <TicketDocument
      ticket={ticket}
      files={files}
      commentaries={commentaries}
      solutions={solutions}
      userCreator={userCreator}
    />
  ).toBlob()

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `ticket-${ticket.id}-${ticket.title.replace(/\s+/g, '-').toLowerCase()}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

export default generatePDF
